/**
 * TrialExpiredLockScreen — Iter 10J UX paywall.
 *
 * Rendered by <OperationalGate> whenever the tenant's SSOT effective
 * status resolves to a non-operational lifecycle bucket (trial_expired,
 * suspended, cancelled, deleted). It is intentionally full-page and
 * self-contained: no operational useEffect / API fetch runs behind it,
 * so pages like /hotel/today can never crash on `data.alerts` while
 * the tenant is locked out.
 *
 * All copy is Italian and product-truth safe.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CreditCard, LifeBuoy, LogOut, ShieldAlert } from "lucide-react";

const COPY = {
  trial_expired: {
    title: "Il periodo di prova è terminato",
    body: "Per continuare a usare CostControlX devi attivare un abbonamento. I tuoi dati restano salvati, ma l'accesso alle funzioni operative è sospeso fino all'attivazione del piano.",
    tone: "critical",
    testid: "trial-expired-lock",
  },
  cancelled: {
    title: "Abbonamento annullato",
    body: "L'abbonamento a CostControlX è stato annullato. Puoi riattivarlo in qualsiasi momento dalla sezione fatturazione. I tuoi dati restano salvati, ma le funzioni operative sono sospese fino alla riattivazione del piano.",
    tone: "critical",
    testid: "cancelled-lock",
  },
  suspended: {
    title: "Account sospeso",
    body: "Il tuo account è temporaneamente sospeso. I tuoi dati restano salvati. Contatta il supporto o aggiorna il metodo di pagamento per riattivare l'accesso alle funzioni operative.",
    tone: "critical",
    testid: "suspended-lock",
  },
  deleted: {
    title: "Account non attivo",
    body: "Questo account non è più attivo. Contatta il supporto per assistenza.",
    tone: "critical",
    testid: "deleted-lock",
  },
  grace_period: {
    title: "Pagamento in sospeso",
    body: "L'ultimo pagamento non è andato a buon fine. Aggiorna il metodo di pagamento nella sezione fatturazione per continuare a usare CostControlX senza interruzioni.",
    tone: "warn",
    testid: "grace-lock",
  },
};

export default function TrialExpiredLockScreen({ status = "trial_expired", onLogout }) {
  const meta = COPY[status] || COPY.trial_expired;
  const showBilling = status !== "suspended" && status !== "deleted";

  return (
    <div
      className="min-h-[100vh] w-full flex items-center justify-center bg-gradient-to-b from-[#F5F3EE] to-[#EFEEE9] px-4 py-10"
      data-testid={meta.testid}
    >
      <div className="max-w-xl w-full bg-white border border-[#D8D4CB] rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-[#102A43] text-white px-6 py-4 flex items-center gap-3">
          <ShieldAlert className="w-6 h-6" />
          <h1 className="text-lg font-bold" data-testid="trial-lock-title">
            {meta.title}
          </h1>
        </div>
        <div className="px-6 py-6 space-y-5">
          <p className="text-[#20354A] text-base leading-relaxed" data-testid="trial-lock-body">
            {meta.body}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {showBilling && (
              <>
                <Link
                  to="/billing"
                  data-testid="trial-lock-cta-billing"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#0D9488] hover:bg-[#0F766E] text-white text-sm font-semibold shadow-sm transition"
                >
                  <CreditCard className="w-4 h-4" />
                  Attiva abbonamento
                </Link>
                <Link
                  to="/billing"
                  data-testid="trial-lock-cta-plans"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#D8D4CB] bg-[#F5F3EE] hover:bg-[#EFEEE9] text-[#102A43] text-sm font-semibold transition"
                >
                  Vai alla fatturazione
                </Link>
              </>
            )}
            <a
              href="mailto:support@costcontrolx.app?subject=Supporto%20CostControlX"
              data-testid="trial-lock-cta-support"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#D8D4CB] hover:border-[#102A43]/40 text-[#20354A] text-sm font-semibold transition"
            >
              <LifeBuoy className="w-4 h-4" />
              Contatta supporto
            </a>
          </div>

          <div className="pt-4 border-t border-[#E2E8F0] flex flex-wrap items-center justify-between gap-3 text-xs text-[#5E646A]">
            <div>
              <span className="font-semibold text-[#20354A]">Aree ancora accessibili:</span>{" "}
              Abbonamento · Fatturazione · Account · Supporto
            </div>
            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                data-testid="trial-lock-cta-logout"
                className="inline-flex items-center gap-1.5 text-[#5E646A] hover:text-[#20354A]"
              >
                <LogOut className="w-3.5 h-3.5" /> Esci
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
